import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Footer from '@components/Footer';
import PhoneInput from 'react-phone-number-input';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {
  checkCustomerAndBooking,
  checkinBooking,
  login,
} from '@/api/checkin/index';
import 'react-phone-number-input/style.css';
import {getHomeUrl} from '@/utils/getHomeUrl';

type CheckinType = 'reservation' | 'walkin';

export default function SelfCheckinHome() {
  const navigate = useNavigate();
  const {i18n, t} = useTranslation();
  const [searchParams] = useSearchParams();
  const [language, setLanguage] = useState(i18n.language);
  const [phone, setPhone] = useState<string | undefined>('');
  const [email, setEmail] = useState<string>('');
  const [textIndex, setTextIndex] = useState(0);
  const [token, setToken] = useState<string>('');
  const {mutate: loginMutate} = login();
  const [errorMsg, setErrorMsg] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [customer, setCustomer] = useState<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCheckedInDialog, setShowCheckedInDialog] = useState(false);
  const [showFirstVisitDialog, setShowFirstVisitDialog] = useState(false);
  const [checkinType, setCheckinType] = useState<CheckinType>('reservation');
  const [inputType, setInputType] = useState<'phone' | 'email'>('email');
  const [birthYear, setBirthYear] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const birth =
    birthYear && birthMonth && birthDay
      ? `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`
      : '';

  const building = searchParams.get('b') || 'MS-GANGNAM';
  const floor = searchParams.get('f') || '2';

  // 3초마다 교체될 문구들 (순서: 한,중,영,일,태,번)
  const rotatingTexts = [
    '13년의 노하우로 완성하는 신뢰할 수 있는 프리미엄 케어', // ko
    '13年的专业经验，打造值得信赖的高端护理', // zh-Hans
    'Premium care you can trust, built on 13 years of expertise', // en
    '13年のノウハウで完成した信頼できるプレミアムケア', // jp
    '13年的專業經驗，打造值得信賴的高端護理', // zh-Hant
    'ดูแลระดับพรีเมียมที่คุณวางใจได้ จากประสบการณ์ 13 ปี', // th
  ];

  useEffect(() => {
    if (building && floor) {
      localStorage.setItem('building', building);
      localStorage.setItem('floor', floor);
    }
  }, [building, floor]);

  useEffect(() => {
    loginMutate(
      {branch: building, floor_id: Number(floor)},
      {
        onSuccess: response => {
          setToken(response.data);
          localStorage.setItem(
            'token',
            JSON.stringify({
              access_token: response.data,
              refresh_token: response.data,
            }),
          );
        },
        onError: error => {
          setErrorMsg(`${t('home.error_message.invalid_token')} ${error}`);
        },
      },
    );
  }, [loginMutate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTextIndex(prev => (prev + 1) % rotatingTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const {mutate: searchMutate} = checkCustomerAndBooking({
    phone,
    email,
    birth,
  });
  const handleSearch = () => {
    if (!token) {
      setErrorMsg(t('home.error_message.invalid_token'));
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    setCustomer(null);

    searchMutate(
      {phone, email, birth},
      {
        onSuccess: response => {
          if (!response.data?.customer) {
            setShowFirstVisitDialog(true);
            return;
          }

          setCustomer(response.data.customer);
          setCheckinType(response.data.hasBooking ? 'reservation' : 'walkin');
          setShowConfirmDialog(true);
        },
        onError: error => {
          setErrorMsg(`${t('home.error_message.search')} ${error}`);
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );
  };

  const {mutate: bookingMutate} = checkinBooking();
  const handleCheckIn = () => {
    if (!token) {
      setErrorMsg(t('home.error_message.invalid_token'));
      return;
    }

    setIsLoading(true);
    setErrorMsg('');
    setShowConfirmDialog(false);

    const requestData = {
      customer_id: customer.id,
      walk_in_available: checkinType === 'walkin',
    };

    bookingMutate(requestData, {
      onSuccess: () => {
        setShowCheckedInDialog(true);
      },
      onError: () => {
        setErrorMsg(t('home.error_message.checkin'));
      },
      onSettled: () => {
        setIsLoading(false);
      },
    });
  };

  const isFormValid =
    ((phone && phone.trim() !== '') || email.trim() !== '') && birth !== '';

  const updateInputTypeByLanguage = (lang: string) => {
    setInputType(lang === 'ko' ? 'phone' : 'email');
    console.log(
      `Language: ${lang} / inputType: ${lang === 'ko' ? 'phone' : 'email'}`,
    );
  };

  useEffect(() => {
    if (!language) {
      setShowLanguageSelector(true);
    } else {
      updateInputTypeByLanguage(language);
    }
  }, []);

  const [showLanguageSelector, setShowLanguageSelector] = useState(
    !localStorage.getItem('lang'),
  );

  const handleSelectLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setLanguage(lang);
    localStorage.setItem('lang', lang);
    setShowLanguageSelector(false);
    updateInputTypeByLanguage(lang);
  };

  return (
    <div
      className='relative w-full h-screen bg-cover bg-center flex flex-col items-center justify-center text-black px-4'
      style={{backgroundImage: "url('/assets/muse-bg.png')"}}>
      {/* 로고 + 문구 */}
      <div className='flex flex-col items-center mb-12 w-full'>
        <img
          src='/assets/muse-logo.png'
          alt='Muse Clinic'
          className='w-full h-auto mb-4 max-w-md'
        />
        <p className='text-center text-lg sm:text-base'>
          {rotatingTexts[textIndex]}
        </p>
      </div>

      <button
        onClick={() => setShowLanguageSelector(true)}
        className='w-full max-w-sm bg-transparent border border-black border-[2px] text-black py-2 rounded mb-6 space-y-2 text-base'>
        {t('home.language_select.button_title') || 'Change language'}
      </button>

      <div className='w-full max-w-md space-y-3'>
        <label className='block text-lg font-medium text-center text-gray-800'>
          {t('home.returningVisitor')}
        </label>
        <div className='flex gap-2 mb-3'>
          <button
            onClick={() => setInputType('phone')}
            className={`flex-1 py-2 rounded border ${inputType === 'phone' ? 'bg-black text-white' : 'bg-white text-black border-gray-400'}`}>
            {t('home.form.title.phone')}
          </button>
          <button
            onClick={() => setInputType('email')}
            className={`flex-1 py-2 rounded border ${inputType === 'email' ? 'bg-black text-white' : 'bg-white text-black border-gray-400'}`}>
            {t('home.form.title.email')}
          </button>
        </div>
        {inputType === 'phone' && (
          <PhoneInput
            placeholder={t('home.form.placeholder.phone')}
            defaultCountry='KR'
            value={phone}
            onChange={value => setPhone(value)}
            className='w-full border border-gray-300 rounded px-3 py-2 focus:outline-none bg-white text-lg h-[3.5rem]'
          />
        )}
        {inputType === 'email' && (
          <input
            id='email'
            type='email'
            placeholder={t('home.form.placeholder.email')}
            className='w-full border border-gray-300 rounded px-3 py-2 text-lg h-[3.5rem]'
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        )}
        {/* 생년월일 선택 */}
        <label className='block text-base font-medium text-gray-800'>
          {t('home.form.birth.title')}
        </label>
        <div className='flex gap-2'>
          <select
            value={birthYear}
            onChange={e => setBirthYear(e.target.value)}
            className='flex-1 border border-gray-300 rounded px-3 py-2 text-lg h-[3.5rem]'>
            <option value=''>{t('home.form.birth.year')}</option>
            {[...Array(100)].map((_, i) => {
              const year = new Date().getFullYear() - i;
              return (
                <option key={year} value={year}>
                  {year}
                </option>
              );
            })}
          </select>

          <select
            value={birthMonth}
            onChange={e => setBirthMonth(e.target.value)}
            className='w-[6rem] border border-gray-300 rounded px-3 py-2 text-lg h-[3.5rem]'>
            <option value=''>{t('home.form.birth.month')}</option>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>

          <select
            value={birthDay}
            onChange={e => setBirthDay(e.target.value)}
            className='w-[6rem] border border-gray-300 rounded px-3 py-2 text-lg h-[3.5rem]'>
            <option value=''>{t('home.form.birth.day')}</option>
            {[...Array(31)].map((_, i) => (
              <option key={i + 1} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleSearch}
          className='w-full bg-black text-white py-4 rounded mt-4 text-lg'
          disabled={!isFormValid || isLoading}>
          {t('home.form.button.title')}
        </button>

        {errorMsg && <div className='text-red-500 text-s'>{errorMsg}</div>}
      </div>

      {showConfirmDialog && customer && (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50'>
          <div className='w-full max-w-sm bg-white p-6 rounded shadow-lg text-center'>
            <h2 className='text-lg font-bold'>
              {checkinType === 'walkin'
                ? t('home.dialog_message.walkin.no_booking_title', {
                    name: customer.name,
                  })
                : t('home.dialog_message.confirm.title', {name: customer.name})}
            </h2>
            <p className='mt-3 text-base leading-relaxed text-gray-700 whitespace-pre-line'>
              {checkinType === 'walkin'
                ? t('home.dialog_message.walkin.no_booking_description')
                : t('home.dialog_message.confirm.description')}
            </p>
            <div className='mt-6 space-y-2'>
              <button
                className='w-full bg-black text-white py-3 rounded'
                onClick={handleCheckIn}>
                {checkinType === 'walkin'
                  ? t('home.dialog_message.walkin.start_checkin')
                  : t('home.dialog_message.confirm.confirm')}
              </button>
              <button
                className='w-full bg-gray-200 text-black py-3 rounded border border-gray-200'
                onClick={() => setShowConfirmDialog(false)}>
                {t('home.dialog_message.confirm.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCheckedInDialog && (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50'>
          <div className='bg-white p-6 rounded shadow-lg'>
            <p className='text-lg font-bold'>
              {t('home.dialog_message.walkin.checked_in')}
            </p>
            <button
              className='w-full bg-black text-white py-3 mt-4 rounded'
              onClick={() => {
                setShowCheckedInDialog(false);
                navigate(getHomeUrl());
              }}>
              {t('home.dialog_message.common.confirm')}
            </button>
          </div>
        </div>
      )}

      {showFirstVisitDialog && (
        <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-50'>
          <div className='w-full max-w-sm bg-white p-6 rounded shadow-lg text-center'>
            <p className='text-lg font-bold'>
              {t('home.dialog_message.walkin.no_customer_title')}
            </p>
            <p className='mt-2 text-base text-gray-700'>
              {t('home.dialog_message.walkin.no_customer_description')}
            </p>
            <div className='mt-4 flex gap-2'>
              <button
                className='w-1/2 bg-black text-white py-3 rounded'
                onClick={() => {
                  navigate('/create-chart', {
                    state: {phone, email, birth},
                  });
                }}>
                {t('home.dialog_message.walkin.start_first_visit')}
              </button>
              <button
                className='w-1/2 bg-gray-300 text-black py-3 rounded border-solid border border-gray-300'
                onClick={() => setShowFirstVisitDialog(false)}>
                {t('home.dialog_message.common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLanguageSelector && (
        <div
          className='fixed inset-0 bg-white flex flex-col items-center justify-center z-50 bg-cover bg-center'
          style={{backgroundImage: "url('/assets/muse-bg.png')"}}>
          {/* 로고 + 문구 */}
          <div className='flex flex-col items-center mb-12 w-full'>
            <img
              src='/assets/muse-logo.png'
              alt='Muse Clinic'
              className='w-full h-auto mb-4 max-w-md'
            />
            <p className='text-center text-lg sm:text-base'>
              {rotatingTexts[textIndex]}
            </p>
          </div>
          <div className='grid grid-cols-2 gap-4 w-full max-w-sm'>
            <button
              onClick={() => handleSelectLanguage('ko')}
              className='py-3 rounded bg-black text-white text-lg'>
              🇰🇷 한국어
            </button>
            <button
              onClick={() => handleSelectLanguage('en')}
              className='py-3 rounded bg-black text-white text-lg'>
              🌐 English
            </button>
            <button
              onClick={() => handleSelectLanguage('zh-Hans')}
              className='py-3 rounded bg-black text-white text-lg'>
              🇨🇳 中文 (简体)
            </button>
            <button
              onClick={() => handleSelectLanguage('zh-Hant')}
              className='py-3 rounded bg-black text-white text-lg'>
              🇹🇼 中文 (繁體)
            </button>
            <button
              onClick={() => handleSelectLanguage('ja')}
              className='py-3 rounded bg-black text-white text-lg'>
              🇯🇵 日本語
            </button>
            <button
              onClick={() => handleSelectLanguage('th')}
              className='py-3 rounded bg-black text-white text-lg'>
              🇹🇭 ภาษาไทย
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
